// Aba "Confirmação Pendente" do /caixa — O CERCO DO PIX (DA-248).
//
// Título: "Vendas Pendentes de Confirmação de Pagamento Integral". Lista as vendas FINALIZADAS SEM
// nota (caso 3 do desenho): o pagamento eletrônico (Pix/cartão) ainda não foi confirmado/casado, então
// a NOTA ESPEROU. De QUALQUER data (trava contra nota esquecida = sonegação; princípio DA-213).
//
// Cada linha: cliente, valor da venda, QUANTO FALTA confirmar, e a ação que completa o casamento — e
// ao completar, a nota é emitida AUTOMATICAMENTE no servidor (no valor recebido). A ação REUSA o fluxo
// existente da Conciliação Bancária (ConferirDesconhecidoModal — "informe onde o dinheiro caiu") em vez
// de reinventar; pro Pix capturado solto, o link "Vincular na Conciliação" leva à aba que já tem o
// VincularModal ancorado no recebimento. Some sozinha quando a nota sai (a flag é zerada no servidor).
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Loader2, FileWarning } from 'lucide-react';
import { api } from '../../lib/api';
import { useSocket } from '../../lib/socket';
import { useAviso } from '../../components/Aviso';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import { formatMoeda, formatDataHoraSP } from '@fiodecobre/shared';
import { ConferirDesconhecidoModal, type Desconhecido, type ContaBanco } from './ConciliacaoBancariaTab';

// Abaixo disso a linha esta CONFIRMADA — o dinheiro entrou e so a nota nao saiu. Mesma tolerancia
// de centavo que o resto do cerco usa pra decidir "fechou".
const FALTA_ZERO = 0.005;

interface AguardandoNota {
  orcamento_id: number;
  venda_id: number | null;
  numero: string;
  venda_numero: string | null;
  cliente_nome: string;
  vendedor_nome: string | null;
  finalizada_em: string | null;
  pix_falta: string;
  cartao_falta: string;
  falta: string;
}

export function ConfirmacaoPendenteTab({ podeConferir }: { podeConferir: boolean }) {
  const navigate = useNavigate();
  const aviso = useAviso();
  const toast = useToast();
  const { socket } = useSocket();
  const [linhas, setLinhas] = useState<AguardandoNota[]>([]);
  const [contas, setContas] = useState<ContaBanco[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [conferir, setConferir] = useState<Desconhecido | null>(null);
  const [dispensar, setDispensar] = useState<AguardandoNota | null>(null);
  const [emitindo, setEmitindo] = useState<number | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [rows, cs] = await Promise.all([
        api.get<AguardandoNota[]>('/api/conciliacao/aguardando-nota'),
        api.get<ContaBanco[]>('/api/conciliacao/contas-banco').catch(() => [] as ContaBanco[]),
      ]);
      setLinhas(rows);
      setContas(cs);
    } catch (e: any) {
      aviso.erro(e.message);
    } finally {
      setCarregando(false);
    }
  }, [aviso]);

  useEffect(() => { void carregar(); }, [carregar]);

  // O casamento (em qualquer lugar) que dispara a emissão também emite estes eventos — recarrega a
  // lista pra a venda resolvida sumir sozinha.
  useEffect(() => {
    if (!socket) return;
    const onAtualizado = () => { void carregar(); };
    socket.on('mp:pix_atualizado', onAtualizado);
    socket.on('cartao:aprovado', onAtualizado);
    socket.on('caixa:pedidos_atualizados', onAtualizado);
    return () => {
      socket.off('mp:pix_atualizado', onAtualizado);
      socket.off('cartao:aprovado', onAtualizado);
      socket.off('caixa:pedidos_atualizados', onAtualizado);
    };
  }, [socket, carregar]);

  // A ação "Confirmar pagamento" reusa o ConferirDesconhecidoModal: informa onde o dinheiro caiu e
  // marca conciliado — o servidor, ao ver a conciliação bater, emite a nota no valor recebido. Pro
  // Pix capturado solto, o caminho mais preciso é Vincular na Conciliação (link abaixo).
  function abrirConferir(l: AguardandoNota) {
    const pixFalta = Number(l.pix_falta);
    const cartaoFalta = Number(l.cartao_falta);
    // Se há as duas naturezas pendentes, prioriza a maior (o operador resolve a outra na sequência).
    const forma: 'pix' | 'cartao' = pixFalta >= cartaoFalta ? 'pix' : 'cartao';
    const valor = forma === 'pix' ? l.pix_falta : l.cartao_falta;
    setConferir({
      orcamento_id: l.orcamento_id,
      venda_id: l.venda_id,
      numero: l.venda_numero ?? l.numero,
      venda_numero: l.venda_numero,
      cliente_nome: l.cliente_nome,
      vendedor_nome: l.vendedor_nome,
      forma,
      valor,
      finalizada_em: l.finalizada_em,
      atualizado_em: l.finalizada_em,
    });
  }

  // Linha com falta ZERO: o pagamento ja casou e so a nota nao saiu. Nao passa pelo "Confirmar
  // pagamento" — nao ha o que conciliar, e aquele caminho gravaria uma conciliacao manual de
  // R$ 0,00 (na forma errada, ainda por cima). Aqui so pedimos a emissao.
  async function emitirNota(l: AguardandoNota) {
    setEmitindo(l.orcamento_id);
    try {
      const out = await api.post<{ ok: boolean; numero?: number }>('/api/conciliacao/emitir-nota', {
        orcamento_id: l.orcamento_id,
      });
      toast.sucesso(out.numero ? `Nota ${out.numero} emitida` : 'Nota emitida');
      void carregar();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setEmitindo(null); }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <FileWarning size={18} style={{ color: '#ca8a04' }} />
        <h2 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>
          Vendas Pendentes de Confirmação de Pagamento Integral
        </h2>
        <button type="button" className="btn-secundario" style={{ marginLeft: 'auto' }} onClick={() => void carregar()} disabled={carregando}>
          {carregando ? <Loader2 size={14} className="animate-spin" style={{ display: 'inline' }} /> : <RefreshCw size={14} style={{ display: 'inline' }} />}{' '}
          Atualizar
        </button>
      </div>

      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12, lineHeight: 1.5 }}>
        Estas vendas foram finalizadas, mas a <strong>nota fiscal ainda não foi emitida</strong> porque o
        pagamento eletrônico (Pix/cartão) não foi confirmado. Assim que o pagamento for casado, a nota é
        emitida automaticamente no valor recebido. Dinheiro e crediário não entram aqui.
      </div>

      {linhas.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
          {carregando ? 'Carregando…' : 'Nenhuma venda aguardando confirmação de pagamento. 🎉'}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--input-border, #e5e7eb)', textAlign: 'left' }}>
                <th style={{ padding: '8px 10px' }}>Venda</th>
                <th style={{ padding: '8px 10px' }}>Cliente</th>
                <th style={{ padding: '8px 10px' }}>Vendedor</th>
                <th style={{ padding: '8px 10px' }}>Finalizada</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Falta confirmar</th>
                <th style={{ padding: '8px 10px', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map((l) => {
                const pixFalta = Number(l.pix_falta);
                const cartaoFalta = Number(l.cartao_falta);
                const confirmada = Number(l.falta) <= FALTA_ZERO;
                return (
                  <tr key={l.venda_id ?? l.orcamento_id} style={{ borderBottom: '1px solid var(--input-border, #eee)' }}>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{l.venda_numero ?? l.numero}</td>
                    <td style={{ padding: '8px 10px' }}>{l.cliente_nome}</td>
                    <td style={{ padding: '8px 10px' }}>
                      {l.vendedor_nome ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ padding: '8px 10px' }}>{formatDataHoraSP(l.finalizada_em)}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>
                      {formatMoeda(l.falta)}
                      <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--text-muted)' }}>
                        {pixFalta > FALTA_ZERO && <>Pix {formatMoeda(pixFalta)}</>}
                        {pixFalta > FALTA_ZERO && cartaoFalta > FALTA_ZERO && ' · '}
                        {cartaoFalta > FALTA_ZERO && <>Cartão {formatMoeda(cartaoFalta)}</>}
                        {confirmada && 'pagamento confirmado — falta só a nota'}
                      </div>
                    </td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {/* Falta ZERO não pede confirmação de pagamento: o dinheiro já entrou. Oferecer
                          "Confirmar pagamento" aqui manda conciliar o que já está conciliado. */}
                      {podeConferir && confirmada && (
                        <button
                          type="button"
                          className="btn-primario"
                          style={{ marginRight: 6 }}
                          onClick={() => void emitirNota(l)}
                          disabled={emitindo === l.orcamento_id}
                        >
                          {emitindo === l.orcamento_id
                            ? <Loader2 size={14} className="animate-spin" style={{ display: 'inline' }} />
                            : 'Emitir nota'}
                        </button>
                      )}
                      {podeConferir && !confirmada && (
                        <button type="button" className="btn-primario" style={{ marginRight: 6 }} onClick={() => abrirConferir(l)}>
                          Confirmar pagamento
                        </button>
                      )}
                      {!confirmada && pixFalta > FALTA_ZERO && (
                        <button
                          type="button"
                          className="btn-secundario"
                          style={{ marginRight: 6 }}
                          title="Abrir a Conciliação Bancária pra vincular um Pix capturado a esta venda"
                          onClick={() => navigate('/caixa', { state: { aba: 'conciliacao' } })}
                        >
                          Vincular na Conciliação
                        </button>
                      )}
                      {podeConferir && l.venda_id != null && (
                        <button
                          type="button"
                          className="btn-secundario"
                          title="Esta venda não vai ter nota fiscal — registra o motivo e tira da fila"
                          onClick={() => setDispensar(l)}
                        >
                          Dispensar nota
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {conferir && (
        <ConferirDesconhecidoModal
          alvo={conferir}
          contas={contas}
          onClose={() => setConferir(null)}
          onConferido={() => { void carregar(); }}
        />
      )}

      {dispensar && (
        <DispensarNotaModal
          alvo={dispensar}
          onClose={() => setDispensar(null)}
          onDispensado={() => { void carregar(); }}
        />
      )}
    </div>
  );
}

// Dispensar a nota é o único jeito de uma venda sair desta fila SEM documento fiscal — então o
// motivo é obrigatório e fica gravado com quem e quando (migration 0231). Sem isso, meses depois a
// venda sem nota é indistinguível de um bug que ninguém viu. O texto do modal diz o que a ação faz
// de verdade, inclusive a parte irreversível pela tela.
function DispensarNotaModal({ alvo, onClose, onDispensado }: {
  alvo: AguardandoNota;
  onClose: () => void;
  onDispensado: () => void;
}) {
  const toast = useToast();
  const aviso = useAviso();
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const curto = motivo.trim().length < 5;

  async function confirmar() {
    setSalvando(true);
    try {
      await api.post('/api/conciliacao/dispensar-nota', { venda_id: alvo.venda_id, motivo: motivo.trim() });
      toast.sucesso(`Nota dispensada — ${alvo.venda_numero ?? alvo.numero}`);
      onDispensado();
      onClose();
    } catch (e: any) { aviso.erro(e.message); }
    finally { setSalvando(false); }
  }

  return (
    <Modal aberto titulo={`Dispensar a nota — ${alvo.venda_numero ?? alvo.numero}`} onFechar={onClose} largura={520}
      rodape={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn-secundario" onClick={onClose} disabled={salvando}>Cancelar</button>
          <button type="button" className="btn-primario" onClick={() => void confirmar()} disabled={salvando || curto}>
            {salvando ? <Loader2 size={14} className="animate-spin" style={{ display: 'inline' }} /> : 'Dispensar nota'}
          </button>
        </div>
      }
    >
      <p style={{ margin: '0 0 12px', fontSize: 13 }}>
        Esta venda <strong>não terá nota fiscal</strong>. Ela sai desta fila e também não volta a
        aparecer na aba "Com problemas". A venda em si continua existindo, com o pagamento no lugar.
      </p>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
        Por que esta venda fica sem nota?
      </label>
      <textarea
        value={motivo}
        onChange={(e) => setMotivo(e.target.value)}
        rows={3}
        style={{ width: '100%', resize: 'vertical' }}
        placeholder="Ex.: cartão conciliado em 01/08, nota retroativa não será emitida"
      />
      {/* Botão desabilitado DIZ por que — o placeholder some no primeiro caractere digitado. */}
      <div style={{ fontSize: 11, marginTop: 6, color: curto ? '#b45309' : 'var(--text-muted)' }}>
        {curto
          ? 'Escreva o motivo (pelo menos 5 caracteres) — ele fica gravado com seu nome e a data.'
          : 'Fica gravado com seu nome e a data.'}
      </div>
    </Modal>
  );
}
